// Utility functions for time display and conversion

export function formatScheduleTime(
  hour: number,
  minute: number
): {
  utcTime: string;
  localTime: string;
  localTimeZone: string;
} {
  // Create a date object for today at the specified UTC time
  const utcDate = new Date();
  utcDate.setUTCHours(hour, minute, 0, 0);

  // Format UTC time
  const utcTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} UTC`;

  // Format local time
  const localTime = utcDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  // Get timezone abbreviation
  const localTimeZone =
    new Intl.DateTimeFormat('en', { timeZoneName: 'short' })
      .formatToParts(utcDate)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return {
    utcTime,
    localTime,
    localTimeZone,
  };
}

export function getNextExecutionTime(daysOfWeek: number[], hour: number, minute: number): Date {
  const now = new Date();
  const currentDay = now.getUTCDay();
  const currentHour = now.getUTCHours();
  const currentMinute = now.getUTCMinutes();

  // Find the next scheduled day
  let daysToAdd = 0;
  let found = false;

  for (let i = 0; i < 7; i++) {
    const checkDay = (currentDay + i) % 7;
    if (daysOfWeek.includes(checkDay)) {
      // If it's today, check if the time has passed
      if (i === 0) {
        if (hour > currentHour || (hour === currentHour && minute > currentMinute)) {
          found = true;
          break;
        }
      } else {
        daysToAdd = i;
        found = true;
        break;
      }
    }
  }

  // If no day found in the next week, use the first scheduled day of next week
  if (!found && daysOfWeek.length > 0) {
    const firstScheduledDay = Math.min(...daysOfWeek);
    daysToAdd = 7 - currentDay + firstScheduledDay;
  }

  const nextExecution = new Date();
  nextExecution.setUTCDate(nextExecution.getUTCDate() + daysToAdd);
  nextExecution.setUTCHours(hour, minute, 0, 0);

  return nextExecution;
}

export function formatNextExecution(nextExecution: Date): string {
  const now = new Date();
  const diff = nextExecution.getTime() - now.getTime();

  if (diff < 0) {
    return 'Past due';
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours < 24) {
    return `in ${hours}h ${minutes}m`;
  } else {
    const days = Math.floor(hours / 24);
    return `in ${days}d ${hours % 24}h`;
  }
}

export function getNextNExecutions(
  daysOfWeek: number[],
  hour: number,
  minute: number,
  count: number = 5
): Date[] {
  if (daysOfWeek.length === 0) {
    return [];
  }

  const executions: Date[] = [];
  const now = new Date();
  let currentDate = new Date(now);

  // Start from today
  const currentDay = now.getUTCDay();
  const currentHour = now.getUTCHours();
  const currentMinute = now.getUTCMinutes();

  // Check if we can include today's execution
  if (daysOfWeek.includes(currentDay)) {
    if (hour > currentHour || (hour === currentHour && minute > currentMinute)) {
      const todayExecution = new Date();
      todayExecution.setUTCHours(hour, minute, 0, 0);
      executions.push(todayExecution);
    }
  }

  // Keep adding days until we have enough executions
  let daysChecked = 0;
  currentDate.setUTCDate(currentDate.getUTCDate() + 1); // Start from tomorrow

  while (executions.length < count && daysChecked < 365) {
    // Safety limit
    const dayOfWeek = currentDate.getUTCDay();

    if (daysOfWeek.includes(dayOfWeek)) {
      const execution = new Date(currentDate);
      execution.setUTCHours(hour, minute, 0, 0);
      executions.push(execution);
    }

    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    daysChecked++;
  }

  return executions.slice(0, count);
}

export function formatLastExecution(lastExecutionTime: string | null | undefined): string {
  if (!lastExecutionTime) {
    return 'Never';
  }

  const lastExec = new Date(lastExecutionTime);
  const now = new Date();
  const diff = now.getTime() - lastExec.getTime();

  if (diff < 0) {
    return 'Just now';
  }

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) {
    return 'Just now';
  } else if (minutes < 60) {
    return `${minutes}m ago`;
  } else if (hours < 24) {
    return `${hours}h ago`;
  } else {
    return `${days}d ago`;
  }
}
